import { Box, Tooltip, TooltipProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Typography, { TypographyVariant } from '@ui/Typography';

const useStyles = makeStyles<{ color?: string }>()((theme, { color }) => ({
  container: {
    color: color || theme.palette.grey[500],
    marginLeft: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: theme.spacing(1.8),
    height: theme.spacing(1.8),
    borderRadius: '50%',
    border: `1px solid ${color || theme.palette.grey[500]}`,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 11,
    padding: 0.5,
    '&:hover': {
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.common.white}`,
    },
  },
  tooltip: {
    '& .MuiTooltip-tooltip': {
      backgroundColor: theme.palette.grey[900],
      color: theme.palette.common.white,
      borderRadius: 0,
      marginBottom: theme.spacing(0.5),
      border: `1px solid ${theme.palette.grey[800]}`,
    },
  },
}));

export interface QuestionMarkIconProps
  extends TitleProps,
    Omit<TooltipProps, 'onClick' | 'title' | 'children'> {
  contentWidth?: string | number;
  placement?: TooltipProps['placement'];
  color?: string;
  typographyVariant?: TypographyVariant;
}

interface TitleProps {
  text: string;
  href: string;
  ctaText: string;
  ctaPlacement?: 'prefix' | 'suffix';
  onClick?: () => void;
}

const TitleText = ({
  text,
  href,
  ctaText,
  ctaPlacement,
  onClick,
}: TitleProps) => {
  if (ctaPlacement === 'prefix')
    return (
      <>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
        >
          {ctaText}
        </a>{' '}
        {text}
      </>
    );

  return (
    <>
      {text}{' '}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
      >
        {ctaText}
      </a>
    </>
  );
};

/**
 * This icon doubles as a tooltip to display given text and CTA url
 */
const QuestionMarkIcon = (props: QuestionMarkIconProps) => {
  const {
    text,
    href,
    onClick,
    ctaText,
    placement = 'top-end',
    contentWidth = 'inherit',
    ctaPlacement = 'suffix',
    typographyVariant,
    color,
    ...rest
  } = props;
  const { classes } = useStyles({ color });

  return (
    <Tooltip
      placement={placement}
      title={
        <Box width={contentWidth}>
          <Typography variant={typographyVariant || 'pMd'}>
            <TitleText
              text={text}
              ctaText={ctaText}
              href={href}
              ctaPlacement={ctaPlacement}
              onClick={onClick}
            />
          </Typography>
        </Box>
      }
      classes={{ popper: classes.tooltip }}
      {...rest}
    >
      <Box component="button" tabIndex={0} className={classes.container}>
        ?
      </Box>
    </Tooltip>
  );
};

export default QuestionMarkIcon;
